import { includes, path } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { replaceData, setValue } from '@gef-ui/form-core/stream/transfer/control';

import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import contactDetail from 'features/views/contactDetail';
import { bruttoProdStatusValues, SUCCESSFULLY_EXECUTED_CONTACT_STATE } from 'features/views/contactList/constants';
import user from 'features/views/user';
import { cleanse } from 'utils';

import * as types from './actionTypes';
import { dialogs, formDataFieldsMap } from './constants';
import { CONTACTS_FORM_NAME, FIELDS } from './form';
import { bodyFactory } from './utils';

const REQUEST_SIZE = 100;

export const setContacts = createAction(types.SET_CONTACTS, (keyWindow, contacts) => ({ keyWindow, contacts }));
const appendContacts = createAction(types.APPEND_CONTACTS, (keyWindow, contacts) => ({ keyWindow, contacts }));
export const resetContacts = createAction(types.RESET_CONTACTS, (keyWindow) => ({ keyWindow }));

export const replaceContactDetail = createAction(types.REPLACE_CONTACT);

export const replaceContact = (id) => async (dispatch, getState) => {
	await dispatch(contactDetail.actions.getContactDetail(id));
	const contact = contactDetail.selectors.getDetail(getState());
	dispatch(replaceContactDetail(contact));
};

const getData = path(['body', 'data']);

export const prefillFormWithPreviousData =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		const fetchStorePath = `${fetch.constants.urls.contactList}.list`;
		const lastRequest = fetch.selectors.getLastRequest(fetchStorePath)(getState());

		if (!isNilOrEmpty(lastRequest)) {
			const { body } = lastRequest;

			const { orgStruct, solutionStateIds = [] } = body;

			const data = {
				campaign: body?.campaignId,
				channel: orgStruct?.channelId,
				region: orgStruct?.regionId,
				...(!isNilOrEmpty(orgStruct?.agencyIds) && { agency: orgStruct?.agencyIds[0] }),
				manager: orgStruct?.manager,
				seller: orgStruct?.seller,
				ppz: orgStruct?.ppz,
				policyNumber: body?.policyNo,
				product: body?.productName,
				firstName: body?.clientName,
				surname: body?.clientSurname,
				businessName: body?.businessName,
				personIn: body?.personIn,
				businessIn: body?.businessIn,
				municipality: body?.municipality,
				zipCode: body?.zipCode,
				contactState: solutionStateIds[0],
				bruttoProductionStatus: body?.bruttoPairedFilter,
			};
			sendControlNext(CONTACTS_FORM_NAME, replaceData({ data }));
		}
	};

export const fetchContacts =
	({ keyWindow, body, queryParams, append = false, callGtmFilter }) =>
	async (dispatch, getState, { getFormData }) => {
		const response = await dispatch(
			fetch.actions.post(fetch.constants.urls.contactList, {
				takeLatest: true,
				uniqueReqID: keyWindow,
				queryParams,
				body,
			}),
		);

		const data = getData(response);

		if (!data || !Array.isArray(data)) return;

		// GTM call only for myContacts (list keyWindow)
		if (keyWindow === 'list' && callGtmFilter) {
			const data = await getFormData(CONTACTS_FORM_NAME);
			const formData = cleanse(data);
			pushToFilterDataLayer({ filterSection: 'Moje kontakty', formData, formDataFieldsMap });
		}

		dispatch(append ? appendContacts(keyWindow, data) : setContacts(keyWindow, data));
	};

export const loadContacts =
	(keyWindow) =>
	(formData, sort, withProduction, calculateBruttoProduction = false, callGtmFilter) =>
	(dispatch, getState) => {
		const currentRole = user.selectors.getCurrentRole(getState());
		const body = bodyFactory(keyWindow, { ...formData, currentRole }, withProduction, calculateBruttoProduction);

		const queryParams = {
			page: 0,
			size: REQUEST_SIZE,
			sort,
		};
		dispatch(fetchContacts({ keyWindow, body, queryParams, callGtmFilter }));
	};

export const loadPage =
	(keyWindow) =>
	(page, append = false) =>
	(dispatch, getState) => {
		const fetchStorePath = `${fetch.constants.urls.contactList}.${keyWindow}`;
		const { queryParams: originalQueryParams, body } = fetch.selectors.getLastRequest(fetchStorePath)(getState());
		const queryParams = { ...originalQueryParams, page };
		dispatch(fetchContacts({ keyWindow, body, queryParams, append }));
	};

export const loadMore =
	(keyWindow) =>
	(append = true) =>
	(dispatch, getState) => {
		const fetchStorePath = `${fetch.constants.urls.contactList}.${keyWindow}`;
		const { body, queryParams: originalQueryParams } = fetch.selectors.getLastRequest(fetchStorePath)(getState());
		const queryParams = { ...originalQueryParams, page: originalQueryParams.page + 1 };

		dispatch(fetchContacts({ keyWindow, body, queryParams, append }));
	};

export const reload = (keyWindow) => (dispatch, getState) => {
	const fetchStorePath = !isNilOrEmpty(keyWindow)
		? `${fetch.constants.urls.contactList}.${keyWindow}`
		: fetch.constants.urls.contactList;

	const { queryParams, body } = fetch.selectors.getLastRequest(fetchStorePath)(getState());
	dispatch(fetchContacts({ keyWindow, body, queryParams }));
};

export const sortContacts = (keyWindow) => (sort) => async (dispatch, getState) => {
	const fetchStorePath = `${fetch.constants.urls.contactList}.${keyWindow}`;
	const { body, queryParams } = fetch.selectors.getLastRequest(fetchStorePath)(getState());
	const queryParamsSort = { ...queryParams, sort };

	dispatch(fetchContacts({ keyWindow, body, queryParams: queryParamsSort }));
};

export const clearFetchState = (keyWindow) => (dispatch) => {
	dispatch(fetch.actions.clearFetchStore(`${fetch.constants.urls.contactList}.${keyWindow}`));
};

export const changeContactState =
	() =>
	async (dispatch, getState, { getFormFieldValue, sendControlNext }) => {
		const bruttoProductionStatus = await getFormFieldValue({
			formName: CONTACTS_FORM_NAME,
			fieldName: FIELDS.bruttoProductionStatus,
		});

		if (includes(bruttoProductionStatus, [bruttoProdStatusValues.PAIRED, bruttoProdStatusValues.NOT_PAIRED])) {
			sendControlNext(
				CONTACTS_FORM_NAME,
				setValue({ fieldName: FIELDS.contactState, value: SUCCESSFULLY_EXECUTED_CONTACT_STATE }),
			);
		}
	};

export const openDialogTooManyContactsForPrint = () => (dispatch) => {
	return dispatch(dialog.actions.open(dialogs.TOO_MANY_CONTACTS_FOR_PRINT));
};
